import { useEffect, useState } from 'react'
import { ApiClientError, type AuthStep, type ChannelProvider, type QrCode } from '../api/types.js'
import { Alert } from '../ui/Alert.js'

/**
 * Узкий срез клиента: панели нужен только `qr` (барьер 2 — клиент API
 * и есть граница процесса для экранов админки, его двойник в тестах разрешён).
 */
export interface QrClient {
  qr(instanceId: string): Promise<QrCode | null>
  /**
   * Подаёт шаг входа: код, пароль двухфакторной или переход на вход
   * сканированием. У WhatsApp вызывается только последний — и то не
   * вызывается: там весь вход и есть сканирование.
   */
  submitAuth(instanceId: string, step: AuthStep): Promise<void>
}

export interface AuthPanelProps {
  client: QrClient
  instanceId: string
  /**
   * Статус инстанса — источник правды у родителя (он же список инстансов).
   * Панель не решает сама, подключился ли инстанс: `qr()` отвечает `null`
   * и когда код ещё не выдан, и когда он уже не нужен — эти два случая
   * неразличимы без статуса снаружи.
   */
  status?: string
  /** Канал номера: от него зависит и путь входа, и подсказки к нему. */
  provider: ChannelProvider
  /** Вызывается, когда родитель сообщает через `status`, что инстанс подключился. */
  onConnected: () => void
}

/** Сообщение по умолчанию для отказов не от сервера (например, обрыв сети). */
const GENERIC_FAILURE = 'Не удалось получить код для привязки. Проверьте соединение и попробуйте ещё раз.'

// WhatsApp обновляет код примерно раз в двадцать секунд, а сам код живёт
// минуту (QR_TTL_MS на сервере, packages/module-whatsapp/src/qr.ts). Опрос
// реже этого интервала рискует продержать на экране просроченный код
// заметное время; чаще — не даёт ничего, сервер отдаёт тот же код (FR-ADM-005).
const POLL_INTERVAL_MS = 3000

/** Тик обратного отсчёта — секунда достаточно точна для человека, смотрящего на телефон, а не на экран. */
const TICK_INTERVAL_MS = 1000

function secondsLeft(expiresAt: string): number {
  return Math.max(0, Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 1000))
}

/**
 * Как объяснить сканирование кода в каждом канале. Общая формулировка
 * «откройте список устройств» отправляет человека искать его самому,
 * а шаги в приложениях разные.
 */
const QR_INSTRUCTIONS: Record<ChannelProvider, string> = {
  whatsapp:
    'В приложении WhatsApp на телефоне: Настройки → Связанные устройства → Привязка устройства, затем наведите камеру на этот код.',
  telegram:
    'В приложении Telegram на телефоне: Настройки → Устройства → Подключить устройство, затем наведите камеру на этот код.',
}

/** Поля ввода, которыми отвечают на вопрос провайдера. */
const ВВОД: Record<
  'code_pending' | 'password_pending',
  { label: string; hint: string; type: 'text' | 'password'; submit: string; field: string }
> = {
  code_pending: {
    label: 'Код из сообщения',
    hint: 'Код приходит сообщением на сам номер. Если он не подошёл, провайдер пришлёт новый.',
    type: 'text',
    submit: 'Подтвердить код',
    field: 'auth-code',
  },
  password_pending: {
    label: 'Облачный пароль',
    hint: 'Это пароль двухфакторной защиты аккаунта — не код из сообщения.',
    type: 'password',
    submit: 'Войти',
    field: 'auth-password',
  },
}

export function AuthPanel(props: AuthPanelProps) {
  const { client, instanceId, status, provider, onConnected } = props
  const [value, setValue] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [qr, setQr] = useState<QrCode | null>(null)
  // Ошибка запроса кода (FR-ADM-016) — отдельно от общей ошибки экрана
  // инстансов: код мог не выдаться, пока сам список инстансов загрузился
  // нормально, и наоборот.
  const [error, setError] = useState<string | null>(null)
  // Тик секундного таймера отсчёта — само значение не используется, только
  // сам факт его изменения заставляет пересчитать secondsLeft(qr.expiresAt).
  const [, setTick] = useState(0)

  // Растёт по кнопке «Запросить новый код» — включён в зависимости эффекта
  // ниже нарочно: клик обязан вызвать внеочередной опрос немедленно, а не
  // ждать ближайший тик POLL_INTERVAL_MS (FR-ADM-016).
  const [requestedAt, setRequestedAt] = useState(0)

  useEffect(() => {
    // Подключённый инстанс код не меняет — опрашивать его дальше значит
    // впустую грузить сервер с каждой открытой вкладки (FR-ADM-005).
    if (status === 'connected') return

    let cancelled = false

    async function poll() {
      try {
        const next = await client.qr(instanceId)
        if (cancelled) return
        // Успешный ответ — даже null, пока код ещё не выдан, — снимает
        // прежнюю ошибку запроса: сервис снова отвечает нормально.
        setError(null)
        setQr(next)
      } catch (cause) {
        if (cancelled) return
        // Прежний код нарочно остаётся на экране (FR-ADM-017): временный
        // сбой одного опроса не должен обнулять уже показанный код, если
        // он ещё не истёк, — человек продолжает его сканировать.
        setError(cause instanceof ApiClientError ? cause.message : GENERIC_FAILURE)
      }
    }

    void poll()
    const timer = setInterval(() => void poll(), POLL_INTERVAL_MS)

    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [client, instanceId, status, requestedAt])

  // Обратный отсчёт до истечения кода (FR-ADM-016) — секундный тик, пока
  // есть непросроченный код и инстанс ещё не подключился.
  useEffect(() => {
    if (status === 'connected' || qr === null) return
    const timer = setInterval(() => setTick((value) => value + 1), TICK_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [status, qr])

  useEffect(() => {
    if (status === 'connected') onConnected()
  }, [status, onConnected])

  function requestNewCode() {
    // Дублирующий клик, пока предыдущий запрос ещё летит, не страшен:
    // client.qr идемпотентен (просто читает текущий код). Значение времени,
    // а не просто счётчик — на дубль-клик в один и тот же миллисекунд не
    // полагаемся, важно лишь что оно меняется.
    setRequestedAt(Date.now())
  }

  async function submit(step: AuthStep) {
    if (submitting) return
    setSubmitting(true)
    setError(null)
    try {
      await client.submitAuth(instanceId, step)
      // Введённое стирается сразу: код одноразовый, а пароль двухфакторной
      // не должен оставаться в поле ввода дольше, чем нужно.
      setValue('')
    } catch (cause) {
      setError(cause instanceof ApiClientError ? cause.message : GENERIC_FAILURE)
    } finally {
      setSubmitting(false)
    }
  }

  // Инстанс уже подключён — показывать код (пусть и последний полученный)
  // означало бы предлагать сканировать то, что уже не имеет смысла.
  if (status === 'connected') return null

  if (status === 'code_pending' || status === 'password_pending') {
    const поле = ВВОД[status]
    return (
      <div className="qr-panel">
        <form
          className="form-inline"
          onSubmit={(event) => {
            event.preventDefault()
            const введено = value.trim()
            if (введено === '') return
            void submit(
              status === 'code_pending'
                ? { kind: 'code', code: введено }
                : { kind: 'password', password: введено },
            )
          }}
        >
          <div className="field">
            <label htmlFor={`${поле.field}-${instanceId}`}>{поле.label}</label>
            <input
              id={`${поле.field}-${instanceId}`}
              type={поле.type}
              value={value}
              autoComplete="off"
              onChange={(event) => setValue(event.target.value)}
            />
          </div>
          <button type="submit" className="btn btn--primary btn--small" disabled={submitting}>
            {поле.submit}
          </button>
        </form>

        <p className="qr-panel__instructions">{поле.hint}</p>

        {/* Запасной путь для номера, у которого код не доходит: вход
            сканированием с уже работающего устройства. У кода из
            сообщения он не отменяет — просто другой способ ответить. */}
        {status === 'code_pending' && (
          <button
            type="button"
            className="btn btn--small"
            disabled={submitting}
            onClick={() => void submit({ kind: 'qr' })}
          >
            Войти сканированием кода
          </button>
        )}

        {error !== null && <Alert onDismiss={() => setError(null)}>{error}</Alert>}
      </div>
    )
  }

  const expired = qr !== null && secondsLeft(qr.expiresAt) <= 0

  return (
    <div className="qr-panel">
      {qr === null ? (
        <p>Ожидание кода — номер запускается, это может занять несколько секунд.</p>
      ) : (
        <>
          <img src={qr.image} alt="Код для сканирования" />
          <div className="qr-panel__body">
            <p className="qr-panel__countdown">
              {expired
                ? 'Код устарел, запрашивается новый…'
                : `Код действует ещё ${secondsLeft(qr.expiresAt)} с`}
            </p>
            <p className="qr-panel__instructions">{QR_INSTRUCTIONS[provider]}</p>
          </div>
        </>
      )}

      {error !== null && <Alert onDismiss={() => setError(null)}>{error}</Alert>}

      {status === 'failed' && (
        <Alert tone="warn">
          Не удалось подключить устройство по этому коду. Запросите новый и повторите сканирование.
        </Alert>
      )}

      <button type="button" className="btn btn--small" onClick={requestNewCode}>
        Запросить новый код
      </button>
    </div>
  )
}
